// Orders — the one place the UI talks to the orders API.
//
// Every endpoint the order board uses lives here so no component builds a URL of its own.
// The endpoints themselves are documented in server/ai/ordersRoutes.js.
//
// WHAT THE SERVER DECIDES AND THIS FILE NEVER RECOMPUTES:
//   · which statuses an order may move to — it arrives as `canMoveTo` per order and as
//     `transitions` for the whole board, already filtered for whether this account is the owner
//     or a member of staff. A tab left open through a permission change therefore cannot offer a
//     move the server will refuse.
//   · every amount, formatted in that order's own currency. A JOD total is three decimal places
//     and a USD one is two, so dividing a minor-unit integer by 100 in the browser is wrong in
//     Jordan. Read `totalText` / `lineTotalText`; the raw integers are there only for arithmetic.
//   · the local time of each order, in the tenant's own timezone.
import axiosInstance from "../../../services/axiosInstance";

const unwrap = (p) => p.then(({ data }) => data);

// ── the board ────────────────────────────────────────────────────────────────
// Filters are optional and additive: `{ status: "new,accepted" }`, a date range, a channel.
//
// `since` IS WHAT MAKES POLLING CHEAP. There is no websocket in this product, so the board polls.
// Pass the newest `updatedAt` already on screen and only the orders that have MOVED come back —
// a board left open on a counter all day does not re-read the day every few seconds. The response
// always carries the full `counts` regardless, so a column badge is never computed from a partial
// page.
export const listOrders = (params = {}) => unwrap(axiosInstance.get("/ai/orders", { params }));

// One order in full: its lines, the free-text order the agent was told, the address, and the
// conversation it came out of.
export const getOrder = (id) => unwrap(axiosInstance.get(`/ai/orders/${encodeURIComponent(id)}`));

// ── moving it along ──────────────────────────────────────────────────────────
// THE ONE AI WRITE THAT IS NOT OWNER-ONLY. Moving an order is the job of whoever is at the counter,
// so this needs edit rights rather than the account owner. A member of staff cannot set
// `needs_review`: that is the engine's judgement that an order is over the ceiling the owner set.
//
// AND IT IS WHAT THE CUSTOMER HEARS. The agent reads this status straight back to anyone who asks
// where their order is, so a mistake here is not a wrong row in a table — it is a wrong sentence
// spoken to a person. The server refuses any move outside the lifecycle and answers 409 with the
// list of what IS allowed, which is what the UI shows rather than a generic failure.
//
// A reason is kept only for a cancellation: "why" means something only when the answer to the
// customer is that their order is not coming.
export const setOrderStatus = (id, status, reason) =>
  unwrap(axiosInstance.patch(`/ai/orders/${encodeURIComponent(id)}/status`, {
    status,
    ...(reason ? { reason } : {}),
  }));

// PUTTING A WRONG TAP BACK. Not a transition and not a hole in the lifecycle — it restores the stage
// the order actually came from, recorded at the time of the move. One level only: the server clears
// the record as it restores, so an undo cannot be undone and nothing ping-pongs.
//
// WHY THIS EXISTS AT ALL: the status is what a customer is TOLD. A mis-tap is therefore not an untidy
// row, it is the business saying something untrue, and staff need a way to make it honest again.
//
// Offer it only where the order's own `undoTo` is set. A 409 means there was nothing to put back,
// which is the ordinary state of an order nobody has moved.
export const undoOrderStatus = (id) =>
  unwrap(axiosInstance.post(`/ai/orders/${encodeURIComponent(id)}/undo`, {}));

// WHICH BRANCH IT BELONGS TO. An order that ended with none — a delivery to an area no branch serves, or
// one taken before the business named its branches — is flagged `needsBranch` until a person gives it
// one here; and an order can be moved to another branch. The server checks the id against the business's
// own branches and writes the change on the order's history. The whole card comes back.
export const setOrderBranch = (id, branchId) =>
  unwrap(axiosInstance.patch(`/ai/orders/${encodeURIComponent(id)}/branch`, { branchId }));

// ── the settings the board depends on ────────────────────────────────────────
// `autoAccept` is the orders-wait-for-me switch: OFF means every order lands as `new` and waits for
// a person. A flagged order waits whatever this says, so the screen must not promise otherwise.
// THE `/ai` SEGMENT IS NOT OPTIONAL. The route is mounted at /api/ai/catalog/settings and the axios
// baseURL ends at /api, so the path here must carry `ai/` exactly as the orders paths above do. Written
// without it, this 404s on every single board load — and because the board deliberately swallows a
// failed settings read so the page still works, nothing on screen said so: the autoAccept banner simply
// never appeared, for a reason no one could see.
export const getOrderSettings = () => unwrap(axiosInstance.get("/ai/catalog/settings"));

// THE BUSINESS'S WHATSAPP TEMPLATES (2026-10-14), read from WhatsApp by the server for the settings page alone — never
// for the board. The page picks a template from these, and is told what a send does with the ones already kept
// (`kept`). `available: false` when WhatsApp cannot be asked, and the page lets a name be typed as before.
export const getWhatsAppTemplates = () => unwrap(axiosInstance.get("/ai/catalog/whatsapp-templates"));

// HOW MANY THERE ARE, and nothing else (S6 6e): the badge beside "Orders" on every page reads this — an order has
// arrived when the total grows.
export const getOrderCounts = () => unwrap(axiosInstance.get("/ai/orders/counts"));

// ── the conversation an order came out of ─────────────────────────────────────
// Through the doors the WhatsApp and call-history screens use, so who may read a conversation is decided where it
// always has been (orderConversation.js).
export const getWhatsAppMessages = (conversationId) =>
  unwrap(axiosInstance.get(`/whatsapp/conversations/${encodeURIComponent(conversationId)}/messages`));
// A call the assistant answered keeps its transcript under `<call id>_ai` (ai/routes.js, at the end of the call).
export const getCallTranscript = (callId) =>
  unwrap(axiosInstance.get(`/Analytics/calls/${encodeURIComponent(`${callId}_ai`)}/transcript`));

// PARTIAL PATCHES ONLY. A key not sent is left alone and `null` restores the default — so a screen
// must never send a whole settings object it did not mean to assert.
export const saveOrderSettings = (patch) => unwrap(axiosInstance.patch("/ai/catalog/settings", patch));

// ── the business's Meta catalog (O2c D1, server/ai/metaCatalogRoutes.js) ───────
// What Meta holds of the menu and what keeps a thing off the cart, by name. Connect, send everything again, and
// disconnect act at once — they are not part of the page's one Save. A refused connect answers 400 with `fix`, the code
// the card words.
export const getMetaCatalog = () => unwrap(axiosInstance.get("/ai/meta-catalog"));
export const connectMetaCatalog = (body) => unwrap(axiosInstance.post("/ai/meta-catalog/connect", body ?? {}));
export const syncMetaCatalog = () => unwrap(axiosInstance.post("/ai/meta-catalog/sync"));
export const disconnectMetaCatalog = () => unwrap(axiosInstance.post("/ai/meta-catalog/disconnect"));

// ── the words the assistant writes (S-1, server/ai/businessWordsRoutes.js) ──────
// Every set in one language; a correction of one set, all saved or none — refused per word with a code the card words
// (`refused: [{ set, kind, why, max?, other?, card? }]`, answered 400); and one word put back to what the assistant had
// written. A save and a put-back answer with the words as they now stand (`now`).
export const getBusinessWords = (lang) => unwrap(axiosInstance.get("/ai/business-words", { params: lang ? { lang } : {} }));
export const saveBusinessWords = (body) => unwrap(axiosInstance.put("/ai/business-words", body));
export const undoBusinessWord = (body) => unwrap(axiosInstance.post("/ai/business-words/undo", body));

// ── errors ───────────────────────────────────────────────────────────────────
// The server speaks in machine words so the model can phrase them. On screen we show what it said
// rather than inventing a sentence per code — and a 409 from a status move carries `errors.allowed`,
// which is more useful to a person than any wording we could add.
export const apiError = (e, fallback) =>
  e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;

export const apiAllowed = (e) => e?.response?.data?.errors?.allowed ?? null;

// The settings PATCH refuses a field by NAME, and `apiError` above would show only its one-line summary
// and swallow which field. This hands back { field: message } so the page can put the refusal beside the
// control that caused it. Both shapes the server uses are read: an object keyed by field, or a list of
// { field, message } entries.
export const apiFieldErrors = (e) => {
  const errs = e?.response?.data?.errors;
  if (!errs) return null;
  if (Array.isArray(errs)) {
    const out = {};
    for (const x of errs) {
      if (x?.field) { out[x.field] = x.message ?? x.error ?? String(x.code ?? "invalid"); continue; }
      // The settings PATCH answers in plain strings that START with the field — "deliveryFee: not_an_amount —
      // write it as 0.300" — so the field is the first word and the rest is what to show beside it.
      const m = typeof x === "string" ? /^(\w+)\s*:?\s*(.*)$/.exec(x) : null;
      if (m) out[m[1]] = m[2] || x;
    }
    return Object.keys(out).length ? out : null;
  }
  if (typeof errs === "object") {
    const out = {};
    for (const [k, v] of Object.entries(errs)) if (typeof v === "string") out[k] = v;
    return Object.keys(out).length ? out : null;
  }
  return null;
};
